import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Banner.css";

export type BannerTone = "success" | "attention" | "warning" | "error" | "info";
export type BannerVariant = "page" | "inline";

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: BannerTone;
  variant?: BannerVariant;
  title?: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const outlineProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

// Icons match the v23 design: check_circle / notification_important /
// error (outlined) / info — all rendered in the material-icons-outlined style.
const DefaultIcon = ({ tone }: { tone: BannerTone }) => {
  switch (tone) {
    case "success": // check_circle
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12.5l3 3 5-6" />
        </svg>
      );
    case "attention": // notification_important — bell with exclamation
    case "warning":
      return (
        <svg {...outlineProps}>
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          <path d="M12 8v3" />
          <path d="M12 14h.01" />
        </svg>
      );
    case "error": // error — outlined circle with exclamation
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 7v6" />
          <path d="M12 16.5h.01" />
        </svg>
      );
    case "info": // info
    default:
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 11v6" />
          <path d="M12 7h.01" />
        </svg>
      );
  }
};

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  {
    tone = "info",
    variant = "page",
    title,
    icon,
    onDismiss,
    dismissLabel = "Dismiss",
    className,
    children,
    role = "status",
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      role={role}
      className={cx("go4-banner", `go4-banner--${tone}`, `go4-banner--${variant}`, className)}
      {...rest}
    >
      <span className="go4-banner__icon">{icon ?? <DefaultIcon tone={tone} />}</span>
      <div className="go4-banner__content">
        {title ? <div className="go4-banner__title">{title}</div> : null}
        {children ? <div className="go4-banner__message">{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label={dismissLabel}
          className="go4-banner__close"
          onClick={onDismiss}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
});
